import secureLocalStorage from "react-secure-storage";
import React, { useContext } from 'react';
import { Route, Redirect } from 'react-router-dom';
import AuthContext from '../Context/AuthContext';
import TenantHeader from '../Components/Tenant/TenantHeader';
import UserHeader from '../Screens/User/UserHeader';
import SuperAdminHeader from '../Components/SuperAdmin/SuperAdminHeader';

// Base protected route that checks if user is authenticated
const ProtectedRoute = ({ component: Component, ...rest }) => {
  // Use the AuthContext directly
  const auth = useContext(AuthContext);
  
  return (
    <Route
      {...rest}
      render={props => {
        // Check if user is authenticated using context
        if (!auth.currentUser) {
          // Fallback to localStorage for routing
          const loggedClient = secureLocalStorage.getItem('loggedClient') || 'tenant';
          let key = secureLocalStorage.getItem('tenantKey') || secureLocalStorage.getItem('userKey');
          if(!key || key==="null"){
            key=props.match.params.id;
          }
          return (
            <Redirect
              to={{
                pathname: `${process.env.PUBLIC_URL}/${loggedClient}/${key}`,
                state: { from: props.location }
              }}
            />
          );
        }
        
        // If authenticated, render the component
        return <Component {...props} />;
      }}
    />
  );
};

// Route that requires Prime subscription
export const PrimeRoute = ({ component: Component, ...rest }) => {
  // Use the AuthContext directly
  const auth = useContext(AuthContext);
  
  return (
    <Route
      {...rest}
      render={props => {
        // Check if user is authenticated using context
        if (!auth.currentUser) {
          // Fallback to localStorage for routing
          const loggedClient = secureLocalStorage.getItem('loggedClient') || 'tenant';
          const key = secureLocalStorage.getItem('tenantKey') || secureLocalStorage.getItem('userKey');
          
          return (
            <Redirect
              to={{
                pathname: `${process.env.PUBLIC_URL}/${loggedClient}/${key}`,
                state: { from: props.location }
              }}
            />
          );
        }
        
        // Check if user has Prime permission using context
        console.log('PrimeRoute - Checking permission using context');
        const hasPrimePermission = auth.hasPermission('prime');
        console.log('PrimeRoute - Has prime permission:', hasPrimePermission);
        
        if (!hasPrimePermission) {
          // Fallback to localStorage for routing
          const loggedClient = secureLocalStorage.getItem('loggedClient') || 'tenant';
          const key = secureLocalStorage.getItem('tenantKey') || secureLocalStorage.getItem('userKey');
          
          console.log('Redirecting to unauthorized page');
          console.log('Redirect path:', `/${loggedClient}/${key}/unauthorized`);
          
          // Force a redirect to the unauthorized page with proper URL format
          window.location.href = `${process.env.PUBLIC_URL}/${loggedClient}/${key}/unauthorized`;
          return null;
        }
        
        // Determine which header to use based on the URL path
        const isTenantRoute = props.match.path.includes('/tenant/');
        const HeaderComponent = isTenantRoute ? TenantHeader : UserHeader;
        
        // If authenticated and has permission, render the component with appropriate header
        return <HeaderComponent {...props} />;
      }}
    />
  );
};

// Route that requires Tasks module enabled
export const TasksRoute = ({ component: Component, ...rest }) => {
  // Use the AuthContext directly
  const auth = useContext(AuthContext);
  
  return (
    <Route
      {...rest}
      render={props => {
        // Check if user is authenticated using context
        if (!auth.currentUser) {
          // Fallback to localStorage for routing
          const loggedClient = secureLocalStorage.getItem('loggedClient') || 'tenant';
          const key = secureLocalStorage.getItem('tenantKey') || secureLocalStorage.getItem('userKey');
          
          return (
            <Redirect
              to={{
                pathname: `${process.env.PUBLIC_URL}/${loggedClient}/${key}`,
                state: { from: props.location }
              }}
            />
          );
        }
        
        // Check if user has Tasks permission using context
        console.log('TasksRoute - Checking permission using context');
        const hasTasksPermission = auth.hasPermission('tasks');
        console.log('TasksRoute - Has tasks permission:', hasTasksPermission);
        
        if (!hasTasksPermission) {
          // Fallback to localStorage for routing
          const loggedClient = secureLocalStorage.getItem('loggedClient') || 'tenant';
          const key = secureLocalStorage.getItem('tenantKey') || secureLocalStorage.getItem('userKey');
          
          console.log('Redirecting to unauthorized page');
          console.log('Redirect path:', `/${loggedClient}/${key}/unauthorized`);
          
          // Force a redirect to the unauthorized page with proper URL format
          window.location.href = `${process.env.PUBLIC_URL}/${loggedClient}/${key}/unauthorized`;
          return null;
        }
        
        // Determine which header to use based on the URL path
        const isTenantRoute = props.match.path.includes('/tenant/');
        const HeaderComponent = isTenantRoute ? TenantHeader : UserHeader;
        
        // If authenticated and has permission, render the component with appropriate header
        return <HeaderComponent {...props} />;
      }}
    />
  );
};

// Route that requires Admin role
export const AdminRoute = ({ component: Component, ...rest }) => {
  // Use the AuthContext directly
  const auth = useContext(AuthContext);
  
  return (
    <Route
      {...rest}
      render={props => {
        // Check if user is authenticated using context
        if (!auth.currentUser) {
          // Fallback to localStorage for routing
          const loggedClient = secureLocalStorage.getItem('loggedClient') || 'tenant';
          const key = secureLocalStorage.getItem('tenantKey') || secureLocalStorage.getItem('userKey');
          
          return (
            <Redirect
              to={{
                pathname: `${process.env.PUBLIC_URL}/${loggedClient}/${key}`,
                state: { from: props.location }
              }}
            />
          );
        }
        
        // Check if user has Admin permission using context
        console.log('AdminRoute - Checking permission using context');
        console.log('AdminRoute - Current user role:', auth.currentUser?.role);
        const hasAdminPermission = auth.hasPermission('admin');
        console.log('AdminRoute - Has admin permission:', hasAdminPermission);
        
        if (!hasAdminPermission) {
          // Fallback to localStorage for routing
          const loggedClient = secureLocalStorage.getItem('loggedClient') || 'tenant';
          const key = secureLocalStorage.getItem('tenantKey') || secureLocalStorage.getItem('userKey');
          
          console.log('Redirecting to unauthorized page');
          console.log('Redirect path:', `/${loggedClient}/${key}/unauthorized`);
          
          // Force a redirect to the unauthorized page with proper URL format
          window.location.href = `${process.env.PUBLIC_URL}/${loggedClient}/${key}/unauthorized`;
          return null;
        }
        
        // Determine which header to use based on the URL path
        const isTenantRoute = props.match.path.includes('/tenant/');
        const HeaderComponent = isTenantRoute ? TenantHeader : UserHeader;
        
        // If authenticated and has permission, render the component with appropriate header
        return <HeaderComponent {...props} />;
      }}
    />
  );
};

// Route that requires SuperAdmin role
export const SuperAdminRoute = ({ component: Component, ...rest }) => {
  // Use the AuthContext directly
  const auth = useContext(AuthContext);
  
  return (
    <Route
      {...rest}
      render={props => {
        // Check if user is authenticated using context
        if (!auth.currentUser) {
          return (
            <Redirect
              to={{
                pathname: process.env.PUBLIC_URL + "/",
                state: { from: props.location }
              }}
            />
          );
        }
        
        // Check if user has SuperAdmin permission using context
        console.log('SuperAdminRoute - Checking permission using context');
        const hasSuperAdminPermission = auth.hasPermission('superadmin');
        console.log('SuperAdminRoute - Has superadmin permission:', hasSuperAdminPermission);
        console.log('SuperAdminRoute - loggedClient:', secureLocalStorage.getItem('loggedClient'));
        
        if (!hasSuperAdminPermission) {
          console.log('Redirecting to login page - Not a SuperAdmin');
          
          // Force a redirect to the login page
          window.location.href = process.env.PUBLIC_URL + "/";
          return null;
        }
        
        // If authenticated and is SuperAdmin, render the component
        return <SuperAdminHeader {...props} />;
      }}
    />
  );
};

export default ProtectedRoute;